import React, { useState, useEffect } from 'react';
import SummaryApi from '../../../components/common/index';
import './ApplicantList.css';

const ApplicantList = () => {
    const [applicants, setApplicants] = useState([]);
    const [filteredApplicants, setFilteredApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [stats, setStats] = useState({ total: 0, experienced: 0, freshers: 0, uniqueRoles: 0 });
const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};
    const fetchData = async () => {
        try {
            setLoading(true);
            
            // 1. Fetch Jobs
            const jobResponse = await fetch(SummaryApi.getCareers.url);
            const jobResult = await jobResponse.json();
            const jobData = Array.isArray(jobResult) ? jobResult : jobResult.data || [];
            
            const jobMapping = {};
            jobData.forEach(job => {
                const id = job.id || job.job_id || job.career_id;
                jobMapping[String(id)] = job.job_title;
            });

            // 2. Fetch Applicants
            const response = await fetch(SummaryApi.getApplicants.url);
            const result = await response.json();
            const rawApplicants = Array.isArray(result) ? result : result.data || [];
            
            // 3. Merge Job Titles
            const enrichedData = rawApplicants.map(app => ({
                ...app,
                display_role: jobMapping[String(app.job_id)] || "General Application"
            }));

            setApplicants(enrichedData);
            setFilteredApplicants(enrichedData);
            calculateStats(enrichedData); 
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (data) => {
        const expCount = data.reduce((count, app) => {
            const expValue = app.experience || app.experience_years || "0";
            const yrs = parseFloat(expValue);
            return (!isNaN(yrs) && yrs > 0) ? count + 1 : count;
        }, 0);

        const roles = new Set(data.map(a => a.display_role));

        setStats({
            total: data.length,
            experienced: expCount,
            freshers: data.length - expCount,
            uniqueRoles: roles.size
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const filtered = applicants.filter(app => 
            (app.full_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (app.display_role || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (app.city || "").toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredApplicants(filtered);
    }, [searchTerm, applicants]);

    return (
        <div className="db-page">
            <div className="db-banner">
                <div className="db-banner-overlay">
                    <div className="db-container">
                        <header className="db-header-content">
                            <div className="db-title-area">
                                <span className="db-badge">Admin Portal</span>
                                <h1>Applicant Management</h1>
                                <p>Review, filter, and manage your incoming talent pool in real-time.</p>
                            </div>
                        </header>
                    </div>
                </div>
            </div>

            <div className="db-container db-main-content">
                <div className="db-widgets">
                    <div className="db-card-widget">
                        <div className="widget-icon blue">👥</div>
                        <div className="widget-info">
                            <h3>{stats.total}</h3>
                            <p>Total Applicants</p>
                        </div>
                    </div>
                    <div className="db-card-widget">
                        <div className="widget-icon green">💼</div>
                        <div className="widget-info">
                            <h3>{stats.experienced}</h3>
                            <p>Experienced</p>
                        </div>
                    </div>
                    <div className="db-card-widget">
                        <div className="widget-icon orange">🎓</div>
                        <div className="widget-info">
                            <h3>{stats.freshers}</h3>
                            <p>Freshers</p>
                        </div>
                    </div>
                    <div className="db-card-widget">
                        <div className="widget-icon purple">🚀</div>
                        <div className="widget-info">
                            <h3>{stats.uniqueRoles}</h3>
                            <p>Active Roles</p>
                        </div>
                    </div>
                </div>

                <div className="db-table-section">
                    {loading ? (
                        <div className="db-loader"><div className="spinner"></div><p>Loading...</p></div>
                    ) : (
                        <div className="db-table-container">
                            <table className="db-table">
                                <thead>
                                    <tr>
                                        {/* Added Serial Number Header */}
                                        <th style={{ width: '50px' }}>S.No</th>
                                        <th>Candidate</th>
                                        <th>Target Role</th>
                                        <th>Location</th>
                                        <th>Professional Background</th>
                                        <th>CTC (Curr / Exp)</th>
                                        <th>Application Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredApplicants.map((app, index) => {
                                        // Logic to identify if applicant is a Fresher
                                        const experienceValue = app.experience || app.experience_years || "0";
                                        const isFresher = parseFloat(experienceValue) === 0 || !experienceValue || experienceValue === "0";

                                        return (
                                            <tr key={app.job_applicant_id || index}>
                                                {/* Displaying Serial Number */}
                                                <td>{index + 1}</td>
                                                
                                                <td>
                                                    <div className="user-info">
                                                        <div className="user-avatar">{app.full_name ? app.full_name.charAt(0) : "?"}</div>
                                                        <div>
                                                            <div className="user-name">{app.full_name}</div>
                                                            <div className="user-email">{app.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><span className="role-badge">{app.display_role}</span></td>
                                                <td>
                                                    <div className="location-text">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                                                        {app.city || "N/A"}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="role-title">{app.current_designation || 'Fresher'}</div>
                                                    <div className="company-name">{app.current_working_company || 'N/A'}</div>
                                                    <div className="exp-tag">
                                                        {!isFresher ? `${experienceValue} Yrs Exp` : 'Fresher'}
                                                    </div>
                                                </td>
                                                <td>
                                                    {/* Display N/A for CTC if Fresher, else display LPA values */}
                                                    {isFresher ? (
                                                        <span style={{ color: '#94a3b8', fontWeight: '500' }}>N/A</span>
                                                    ) : (
                                                        <div className="ctc-values">
                                                            <span className="curr">{app.current_ctc || '0'} LPA</span>
                                                            <span className="arrow">→</span>
                                                            <span className="exp">{app.expected_ctc || '0'} LPA</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
    <div className="last-visit">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        {formatDate(app.created_date)}
    </div>
</td>
                                                <td>
                                                    {app.resume && app.resume !== "null" && app.resume !== "" ? (
                                                    <a href={`${SummaryApi.ResumeBasePath.url}/${app.resume}`} target="_blank" rel="noreferrer" className="resume-link">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
                                                        Resume
                                                    </a>
                                                    ) : <button className="resume-disabled" disabled>Missing</button>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApplicantList;